import * as React from 'react';

import { Input } from '@independent-software/typeui/controls/Input';
import { Form } from '@independent-software/typeui/controls/Form';
import { Flex } from '@independent-software/typeui/controls/Flex';
import { Tabs } from '@independent-software/typeui/controls/Tabs';
import { MarkdownTextarea } from '@independent-software/typeui/modules/MarkdownTextarea';
import { Label } from '@independent-software/typeui/controls/Label';
import { Dropdown } from '@independent-software/typeui/controls/Dropdown';

import { AuthContext } from '../../AuthContext';
import { Campaign, Project, ProjectFactory } from '../../resource/';
import { Query } from '../../services/';
import { Section } from '../../modules';
import { TidalSituation } from './TidalSituation';

interface IProps {
  /** Initial form data. */
  data: Campaign;
  /** Called whenever form changes. */
  onChange: (data: Campaign, forceupdate: boolean) => void;
  /** Called whenever a field validates. Returns validation state for whole form. */
  onValidate: (valid: boolean) => void;
  /** 
   * Mark form as dirty. If dirty, it will show error messages for all fields,
   * even pristine fields.
   */  
  dirty?: boolean;
}

const CampaignForm = (props: IProps) => {
  const auth = React.useContext(AuthContext);
  const [projects, setProjects] = React.useState([] as Project[]);

  React.useEffect(() => handleProjectSearch(), []);

  const handleProjectSearch = (q?: string) => {
    // Retrieve a list of projects:
    let query = new Query('name', 'asc');
    if (q) query.setFilter('q', 'like', q);
    ProjectFactory.getSome(auth, 0, 16, query)
    .then(res => setProjects(res.items));
  }

  const renderName = () =>
    <Form.Field 
      name="name" label="Name"
      value={props.data.name}
      control={(<Input type="text" placeholder="Name" fluid/>)}
      required={{message: "A campaign name is required."}}
      minLength={{length: 3, message: "Campaign name must be at least 3 characters."}}
      maxLength={{length: 255, message: "Campaign name may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter a campaign name.</React.Fragment>)}/>

  const renderDate = () =>
    <Form.Field
      name="date" label="Date"
      value={props.data.date}
      control={(<Input type="date" nofuture placeholder="Date" fluid/>)}
      hint={(<React.Fragment>Please select a campaign date.</React.Fragment>)}/>

  const renderProject = () => 
    <Form.Field
      width={1} label="Project"
      name="project" 
      value={props.data.project}
      control={(<Dropdown clearable onSearch={handleProjectSearch} fluid data={projects} placeholder="Project" label={(item:Project) => item.name}>
        <Dropdown.Column weight={1}>{(item:Project) => item.name}</Dropdown.Column>
      </Dropdown>)}
      hint="Please select a project."/>  

  const renderDescription = () =>
    <Form.Field 
      name="description" label="Description"
      value={props.data.description}
      control={(<MarkdownTextarea rows={16} fluid/>)}
      hint={(<React.Fragment>Please enter a campaign description.</React.Fragment>)}/>    
            
  const renderFreshwaterDischarge = () =>
    <Form.Field 
      name="freshwater_discharge" label="Freshwater discharge"
      value={props.data.freshwater_discharge}
      control={(<Input type="text" placeholder="Freshwater discharge" fluid><Label attached="right" color="#aaa" pointing>m<sup>3</sup>/s</Label></Input>)}
      isFloat={{message: "Freshwater discharge must be a numeric value."}}
      min={{value: 0, message: "Freshwater discharge must be at least 0."}}
      hint={(<React.Fragment>Please enter the river flow into the system.</React.Fragment>)}/>    

  const renderTidalAmplitude = () =>
    <Form.Field 
      name="tidal_amplitude" label="Tidal amplitude"
      value={props.data.tidal_amplitude}
      control={(<Input type="text" placeholder="Tidal amplitude" fluid><Label attached="right" color="#aaa" pointing>m</Label></Input>)}
      isFloat={{message: "Tidal amplitude must be a numeric value."}}
      hint={(<React.Fragment>Please enter the maximum water height above mid-tide mark.</React.Fragment>)}/>    
      
  const renderTidalSituation = () =>
    <Form.Field 
      name="tidal_situation" label="Tidal situtation"
      value={props.data.tidal_situation}
      control={<Dropdown placeholder="Tidal situation" fluid data={TidalSituation.getList()}
        label={(item) => TidalSituation.toName(item)}>
        <Dropdown.Column>{(item) => TidalSituation.toName(item)}</Dropdown.Column>
      </Dropdown>}
      hint={(<React.Fragment>Please select the tide condition.</React.Fragment>)}/>         

  const renderHighTime = () =>
    <Form.Field 
      name="high_time" label="High water time"
      value={props.data.high_time}
      control={(<Input type="time" placeholder="High water time" fluid is24h clock format="HH:mm"/>)}
      hint={(<React.Fragment>Please select a high water time.</React.Fragment>)}/>          
        
  const renderLowTime = () =>
    <Form.Field 
      name="low_time" label="Low water time"
      value={props.data.low_time}
      control={(<Input type="time" placeholder="Low water time" fluid is24h clock format="HH:mm"/>)}
      hint={(<React.Fragment>Please select a low water time.</React.Fragment>)}/>    

  const renderRadiation = () =>
    <Form.Field 
      name="radiation" label="Solar radiation"
      value={props.data.radiation}
      control={(<Input type="text" placeholder="Solar radiation" fluid><Label attached="right" color="#aaa" pointing>W/m<sup>2</sup></Label></Input>)}
      isFloat={{message: "Solar radiation must be a numeric value."}}
      min={{value: 0, message: "Solar radiation must be at least 0."}}
      hint={(<React.Fragment>Please enter the maximum solar radiation.</React.Fragment>)}/>          
  
  const renderRainfall = () =>
    <Form.Field 
      name="rainfall" label="Rainfall"
      value={props.data.rainfall}
      control={(<Input type="text" placeholder="Rainfall" fluid><Label attached="right" color="#aaa" pointing>mm</Label></Input>)}
      isFloat={{message: "Rainfall must be a numeric value."}}
      min={{value: 0, message: "Cumulative rainfall must be at least 0."}}
      hint={(<React.Fragment>Please enter the cumulative rainfall for the previous five days.</React.Fragment>)}/>

  const renderLowTemperature = () =>
    <Form.Field 
      name="low_temperature" label="Air temperature (min)"
      value={props.data.low_temperature}
      control={(<Input type="text" placeholder="Air temperature (min)" fluid><Label attached="right" color="#aaa" pointing>&ordm;C</Label></Input>)}
      isFloat={{message: "Minimum air temperature must be a numeric value."}}
      hint={(<React.Fragment>Please enter the lowest registered air temperature.</React.Fragment>)}/>

  const renderHighTemperature = () =>
    <Form.Field 
      name="high_temperature" label="Air temperature (max)"
      value={props.data.high_temperature}
      control={(<Input type="text" placeholder="Air temperature (max)" fluid><Label attached="right" color="#aaa" pointing>&ordm;C</Label></Input>)}
      isFloat={{message: "Maximum air temperature must be a numeric value."}}
      hint={(<React.Fragment>Please enter the highest registered air temperature.</React.Fragment>)}/>

  const renderWindDirection = () =>
    <Form.Field 
      name="wind_direction" label="Wind direction"
      value={props.data.wind_direction}
      control={(<Input type="text" placeholder="Wind direction" fluid><Label attached="right" color="#aaa" pointing>deg</Label></Input>)}
      isInt={{message: "Wind direction must be a whole number."}}
      max={{value: 360, message: "Wind direction must be between 0 and 360 degrees."}}
      min={{value: 0, message: "Wind direction must be between 0 and 360 degrees."}}
      hint={(<React.Fragment>Please enter the wind direction in degrees (north is 360).</React.Fragment>)}/>

  const renderWindSpeed = () =>
    <Form.Field 
      name="wind_speed" label="Wind speed"
      value={props.data.wind_speed}
      control={(<Input type="text" placeholder="Wind speed" fluid><Label attached="right" color="#aaa" pointing>m/s</Label></Input>)}
      isFloat={{message: "Wind speed must be a numeric value."}}
      min={{value: 0, message: "Wind direction must be at least 0."}}
      hint={(<React.Fragment>Please enter the wind speed in meters per second.</React.Fragment>)}/>
      
  const renderWeather = () =>
    <Form.Field 
      name="weather" label="Weather"
      value={props.data.weather}
      control={(<Input type="text" placeholder="Weather" fluid></Input>)}
      maxLength={{length: 255, message: "Weather may not be longer than 255 characters."}}
      hint={(<React.Fragment>Please enter the weather conditions (e.g. slightly overcast,warm, light breeze).</React.Fragment>)}/>         

  return (
    <Form
      data={props.data} dirty={props.dirty} onChange={props.onChange} onValidate={props.onValidate}>
      <Tabs underlined>
        <Tabs.Pane label="General">
          <Section padded>
            <Flex.Columns count={2}>
              <>
                {renderName()}
                {renderDate()}
                {renderProject()}
              </>
              <>
                {renderDescription()}
              </>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
        <Tabs.Pane label="Hydrology">
          <Section padded>
            <Flex.Columns count={2}>
              <>{renderFreshwaterDischarge()}</>
              <></>
              <>{renderTidalAmplitude()}</>
              <>{renderTidalSituation()}</>
              <>{renderHighTime()}</>
              <>{renderLowTime()}</>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
        <Tabs.Pane label="Weather">
          <Section padded>
            <Flex.Columns count={2}>
              <>{renderRadiation()}</>
              <>{renderRainfall()}</>
              <>{renderLowTemperature()}</>
              <>{renderHighTemperature()}</>
              <>{renderWindDirection()}</>
              <>{renderWindSpeed()}</>
              <>{renderWeather()}</>
            </Flex.Columns>
          </Section>
        </Tabs.Pane>
      </Tabs>
    </Form>
  )
}

export { CampaignForm }

